/**
 * Le module **Transactions** — la recherche qui manquait sous un droit qui
 * existait (`B-11`, `EX-BO-004`, `EX-LDG-002`).
 *
 * Maquette : artboard `b_transactions` de
 * `designs/client-app/project/Jëliko - Guichet, Back-office, Marchand.dc.html`.
 * `test/app/transactions-maquette.test.tsx` **relit** cette maquette et la
 * confronte au livré, plutôt que d'en recopier les valeurs ici : un attendu
 * dérivé du code éprouvé n'est pas un attendu (`D-34`).
 *
 * ## Ce qui manquait, et ce n'était pas un écran
 *
 * Le module était **déclaré** : une route `/transactions`, une icône, une
 * entrée de menu, un module `TRANSACTIONS` dans `admin.bo_action`, et le droit
 * `TRANSACTION_VIEW` accordé depuis la V7 à six rôles sur sept — refusé au seul
 * `PRODUCT_ADMIN`. Et **aucune route serveur ne servait de transaction au
 * back-office**. C'est la onzième occurrence de la famille de `D-25`, `D-30`,
 * `D-35`, `D-45` et `D-57`, prise par l'autre bout : un **droit accordé que
 * rien ne sert**.
 *
 * ## L'état de l'écran vit dans l'ADRESSE
 *
 * La période, les filtres, la recherche et la page sont dans la chaîne de
 * requête, et l'écran n'en garde aucune copie. Ce n'est pas une commodité :
 * c'est la correction d'une faute mesurée.
 *
 * Avant, `location.search` restait vide. Après une recherche, en ouvrant un
 * résultat puis en revenant, le champ de la barre affichait encore
 * `779982462`, le bandeau avait disparu, et la table rendait « 25 résultats sur
 * 120 » — **la file non filtrée**. L'interface affirmait un filtre qui n'était
 * pas appliqué, et c'est le pire des trois états possibles : un écran vide se
 * remarque, un écran qui ment, non.
 *
 * Trois choses en découlent, et les trois valent d'être dites : le retour en
 * arrière rend exactement l'écran d'avant ; une vue filtrée **s'envoie à un
 * collègue** par son adresse ; et la barre de recherche affiche ce que l'écran
 * cherche réellement, parce qu'elle le lit dans la déclaration plutôt que dans
 * son propre souvenir.
 *
 * ## Le second passage, le dessin et l'écran côte à côte
 *
 * Le porteur a ouvert la page livrée et a nommé six choses. Elles sont traitées
 * ici et dans les écrans voisins :
 *
 *  1. **« on n'arrive pas à différencier les statuts »** — les états étaient du
 *     texte noir. Ils sont des **pastilles colorées**, et la couleur vient du
 *     **serveur** : `statusTone` porte les trois constantes que
 *     `TransactionStatus` déclare ;
 *  2. **« le texte des types est mal formaté »** — les codes sont traduits,
 *     **avec repli sur le code** : un type ajouté par configuration s'affiche
 *     tel quel plutôt que de disparaître ;
 *  3. **les pastilles rendaient l'énumération.** Elles portent le libellé, le
 *     **compte servi**, et elles suivent désormais la recherche — une revue
 *     avait relevé qu'elles annonçaient « Échouée · 3 » quarante points au-dessus
 *     d'une carte qui disait « Échouées 0 » ;
 *  4. **les indicateurs étaient une ligne de texte.** Ce sont des **cartes**,
 *     et elles se recalculent avec les filtres parce que le serveur les
 *     recalcule ;
 *  5. **« la page paraît vide »** — le défaut prenait la journée seule. Mesuré :
 *     trois opérations le jour même contre cent vingt sur sept jours. Le défaut
 *     est passé à **sept jours**, côté serveur, et trois raccourcis de période
 *     laissent élargir d'un geste ;
 *  6. **le panneau dépliable est devenu un ÉCRAN.** Ouvrir une ligne mène à
 *     `TransactionDetailScreen`, et la lecture y est **journalisée une seconde
 *     fois**, avec la référence en objet.
 *
 * ## Cinq colonnes, et deux que le porteur a retirées
 *
 * Le dessin en porte sept. « Enlève frais et Contreparties sur la table » : la
 * décision est du porteur, elle est postérieure au dessin, et elle se lit —
 * deux colonnes de moins laissent enfin respirer le montant et l'état, qui sont
 * ce qu'on vient lire. **L'information n'est pas perdue** : les frais restent en
 * carte d'indicateur, en tête de page, et les deux contreparties sont sur la
 * fiche de l'opération, avec le lien vers la fiche de chaque acteur.
 *
 * C'est une divergence avec la maquette, et elle est **inscrite au registre**
 * plutôt que silencieuse : un test de dessin qu'on aligne sans dire pourquoi est
 * exactement ce que le registre existe pour empêcher.
 *
 * ## Aucun calcul dans le navigateur
 *
 * « Tout doit venir du serveur, aucun calcul côté front. » Le total, le volume,
 * les frais, le taux de succès, le nombre d'opérations par état : tous sortent
 * du même `SELECT` que la page. La raison n'est pas l'élégance — **un chiffre
 * calculé dans le navigateur est un chiffre que le journal d'audit ne peut pas
 * justifier**, et deux écrans qui le calculent chacun finissent par diverger.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Pagination } from '../controls/Pagination.js';
import { FormShell } from '../shell/FormShell.js';
import type { FormFieldDescriptor } from '../shell/FormShell.js';
import { QueueShell } from '../shell/QueueShell.js';
import type { QueueColumn, QueueEntry } from '../shell/QueueShell.js';
import { useDeclareExport } from './export-context.js';
import { useDeclareSearch } from './search-context.js';
import { remettreLeFichier, toCsv } from './export-csv.js';
import { useApiClient } from '../http/api-context.js';
import { ApiError } from '../http/api-client.js';
import { searchTransactions } from '../http/transactions.js';
import type { Transaction, TransactionPage } from '../http/transactions.js';
import { ModuleIcon } from './module-icons.js';
import { StatusPill, transactionRoute, useTransactionFormats } from './transactions-display.js';

/**
 * Les **sept colonnes** — et ce ne sont pas les sept du dessin.
 *
 * Le dessin porte Référence · Heure · Type · Contreparties · Montant · Frais ·
 * Statut. Deux tours de mesure les ont remplacées par : Référence · Heure ·
 * Type · **Canal** · Montant · Statut · **Actions**.
 *
 * **Deux colonnes retirées par le porteur** — « enlève frais et Contreparties
 * sur la table » —, et les chiffres lui donnent raison. Sur les cent vingt
 * opérations de la fenêtre : `fee_amount > 0` sur **une seule**, et la colonne
 * des contreparties tronquait sur vingt-cinq rangées sur vingt-cinq — deux cent
 * cinquante-quatre points coupés en deux boîtes de cent trois, où ne tenaient
 * que treize caractères de nom et douze de numéro, c'est-à-dire l'indicatif
 * national et rien d'autre. L'information n'est pas perdue : les frais sont en
 * carte d'indicateur, les contreparties sur la **fiche**, en entier.
 *
 * **Une colonne ajoutée, et une seule : le canal.** C'est le seul champ de la
 * ligne qui porte une vraie variance — OUTLET 81, APP 28, SYSTEM 8, NETWORK 3
 * sur cent vingt. Les autres candidats ont été mesurés et écartés : la version
 * de plan est absente sur 91 % des opérations, l'écart entre `total_debited` et
 * `amount + fee_amount` sur **100 %** — aucun —, `completed_at` jamais nul, le
 * code d'échec sur trois lignes, le libellé sur dix-sept. Une colonne vide à
 * neuf dixièmes n'est pas une colonne, c'est une marge.
 *
 * **La densité vient des secondes lignes, pas de colonnes de plus**, et chacune
 * n'est rendue que si elle a un contenu — jamais un gabarit vide, jamais un
 * « 0 F » répété. C'est ce qui évite de recréer la troncature qu'on vient de
 * supprimer.
 *
 * **`actions` ne porte pas de donnée** : c'est l'affordance de la rangée
 * cliquable, rendue visible. Voir la cellule pour la raison qui en fait un
 * indicateur et non un bouton.
 */
export const transactionColumns: readonly QueueColumn[] = [
  { id: 'reference', labelKey: 'transactions.columns.reference', emphasis: 'numeric' },
  { id: 'time', labelKey: 'transactions.columns.time', emphasis: 'numeric' },
  { id: 'type', labelKey: 'transactions.columns.type', emphasis: 'muted' },
  { id: 'channel', labelKey: 'transactions.columns.channel', emphasis: 'muted' },
  { id: 'amount', labelKey: 'transactions.columns.amount', emphasis: 'numeric' },
  { id: 'status', labelKey: 'transactions.columns.status', emphasis: 'strong' },
  { id: 'actions', labelKey: 'transactions.columns.actions', emphasis: 'plain' },
];

/**
 * Les colonnes que le fichier exporté porte — **toutes sauf la dernière**.
 *
 * « Actions » ne porte aucune donnée : l'écrire dans un fichier donnerait une
 * colonne d'en-tête suivie de cellules vides, c'est-à-dire une colonne qui
 * promet quelque chose et ne le tient pas — dans un fichier qu'on relira
 * ailleurs, sans l'écran pour l'expliquer.
 */
const colonnesExportees = transactionColumns.filter((colonne) => colonne.id !== 'actions');

/**
 * Les trois raccourcis de période, en **jours**.
 *
 * Le porteur a dit « la page paraît vide », et le chiffre lui donne raison :
 * trois opérations le jour même contre cent vingt sur sept jours. Le défaut est
 * passé à sept jours **côté serveur** — c'est lui qui écrit les instants, c'est
 * donc lui qui sait ce qu'« aujourd'hui » veut dire —, et ces trois raccourcis
 * laissent élargir ou resserrer d'un geste, sans ouvrir deux calendriers.
 *
 * Un jour, sept, trente : ce sont les trois périodes qu'un contrôleur financier
 * regarde — la journée qu'il arrête, la semaine qu'il contrôle, le mois qu'il
 * clôt. Une quatrième valeur serait un contrôle de plus pour une question que
 * personne ne pose.
 */
export const quickRanges: readonly number[] = [1, 7, 30];

/**
 * Les paramètres que l'écran porte dans l'adresse.
 *
 * Ils sont nommés ici, et une seule fois : la lecture, l'écriture et la remise à
 * zéro s'y réfèrent toutes. Une seconde liste écrite à la main serait un
 * paramètre oublié au premier ajout, et un filtre qui survivrait à une remise à
 * zéro sans que personne le voie.
 */
const PARAMETRES = ['from', 'to', 'type', 'status', 'minAmount', 'search', 'page'] as const;

/** Un jour de plus que le dernier rang connu — la première page. */
const PREMIERE_PAGE = 1;

/** L'écran : la période, ses filtres, ses indicateurs, et la table. */
export function TransactionsScreen(): ReactNode {
  const { t } = useTranslation();
  const client = useApiClient();
  const naviguer = useNavigate();
  const [adresse, poserLAdresse] = useSearchParams();
  const {
    montant,
    heure,
    instant,
    journee,
    journeeCourte,
    libelleType,
    libelleEtat,
    libelleCanal,
    libelleInitiateur,
    taux,
  } = useTransactionFormats();

  const [registre, setRegistre] = useState<TransactionPage | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [avis, setAvis] = useState<string | null>(null);

  /**
   * Ce que l'adresse demande, et **rien d'autre**.
   *
   * L'écran ne double pas l'adresse d'un état local : un second souvenir serait
   * un second endroit où le filtre peut être vrai, et c'est exactement ce qui
   * faisait afficher un terme que la table n'appliquait pas.
   *
   * La dépendance est la chaîne de requête et non l'objet : `useSearchParams`
   * rend un objet neuf à chaque rendu, et le comparer par référence relancerait
   * une lecture par rendu — donc une entrée d'audit par rendu.
   */
  const requete = adresse.toString();
  const demande = useMemo(() => {
    const lu = new URLSearchParams(requete);
    const rang = Number.parseInt(lu.get('page') ?? '', 10);
    return {
      from: lu.get('from') ?? undefined,
      to: lu.get('to') ?? undefined,
      type: lu.get('type') ?? '',
      status: lu.get('status') ?? '',
      minAmount: lu.get('minAmount') ?? '',
      search: lu.get('search') ?? '',
      page: Number.isFinite(rang) && rang > 0 ? rang : undefined,
    };
  }, [requete]);

  useEffect(() => {
    let abandonne = false;
    void (async () => {
      try {
        const page = await searchTransactions(client, demande);
        if (!abandonne) {
          setRegistre(page);
          setErreur(null);
        }
      } catch (echec) {
        if (abandonne) return;
        // La page lue est effacée avec l'échec. La garder ferait lire la période
        // PRÉCÉDENTE sous la fenêtre nouvelle — sur un registre d'opérations,
        // c'est la pire confusion possible.
        setRegistre(null);
        setErreur(echec instanceof ApiError ? echec.messageKey : 'errors.server');
      }
    })();
    return () => {
      abandonne = true;
    };
  }, [client, demande]);

  /**
   * Écrit dans l'adresse ce que l'écran doit montrer.
   *
   * Un paramètre vide est **retiré** plutôt qu'écrit vide : `?type=` serait un
   * critère que le serveur devrait interpréter, et une adresse partagée dirait
   * autre chose que ce qu'elle montre.
   *
   * La page repart à un dès qu'un critère change, sauf quand c'est la page
   * elle-même qu'on tourne : garder le rang quatre sous une fenêtre neuve
   * rendrait une page vide qu'on lirait comme « aucune opération ».
   */
  const poser = useCallback(
    (changements: Partial<Record<(typeof PARAMETRES)[number], string>>) => {
      const suivante = new URLSearchParams(requete);
      // La boucle part de la table des paramètres et non des clés reçues : un
      // nom qui ne serait pas de la table n'entre pas dans l'adresse, et une
      // faute de frappe cesse d'y écrire un critère que personne ne relira.
      for (const nom of PARAMETRES) {
        if (!Object.hasOwn(changements, nom)) continue;
        const valeur = changements[nom];
        if (valeur === undefined || valeur === '') suivante.delete(nom);
        else suivante.set(nom, valeur);
      }
      if (!Object.hasOwn(changements, 'page')) suivante.delete('page');
      poserLAdresse(suivante);
    },
    [poserLAdresse, requete],
  );

  const filtrer = useCallback(
    (valeurs: Record<string, string>) => {
      poser({
        from: valeurs.from ?? '',
        to: valeurs.to ?? '',
        type: valeurs.type ?? '',
        status: valeurs.status ?? '',
        minAmount: (valeurs.minAmount ?? '').trim(),
      });
    },
    [poser],
  );

  /**
   * Une période raccourcie — et elle est calculée **sur la fin servie**.
   *
   * Sur `registre.to` et non sur l'horloge du poste : le serveur applique la
   * fenêtre en UTC, et un poste mal réglé, ou simplement à l'ouest du méridien,
   * décalerait la période d'un jour sans que rien ne le dise.
   */
  const appliquerLaPeriode = useCallback(
    (jours: number) => {
      if (registre === null) return;
      const fin = new Date(`${registre.to}T00:00:00Z`);
      const debut = new Date(fin);
      debut.setUTCDate(debut.getUTCDate() - (jours - 1));
      poser({ from: debut.toISOString().slice(0, 10), to: registre.to });
    },
    [poser, registre],
  );

  /** Combien de jours la fenêtre servie couvre, bornes incluses. */
  const joursDeLaFenetre = useMemo(() => {
    if (registre === null) return 0;
    const debut = Date.parse(`${registre.from}T00:00:00Z`);
    const fin = Date.parse(`${registre.to}T00:00:00Z`);
    return Math.round((fin - debut) / 86_400_000) + 1;
  }, [registre]);

  /**
   * Ce que la barre supérieure cherche — **et ce qu'elle affiche**.
   *
   * Le terme déclaré est celui de l'adresse, donc celui que la table applique
   * réellement. C'est ce qui fait qu'un retour en arrière vide le champ quand il
   * vide le filtre, au lieu de laisser la barre affirmer un filtre qui n'existe
   * plus.
   *
   * La recherche part sur `Entrée` et non à la frappe — voir
   * `search-context.tsx` : chercher est un acte journalisé, et une recherche par
   * frappe écrirait une entrée d'audit par lettre.
   */
  const chercher = useCallback(
    (terme: string) => {
      setAvis(
        terme === ''
          ? t('transactions.search.cleared')
          : t('transactions.search.applied', { term: terme }),
      );
      poser({ search: terme });
    },
    [poser, t],
  );
  const recherche = useMemo(
    () => ({ term: demande.search, onSearch: chercher }),
    [chercher, demande.search],
  );
  useDeclareSearch(recherche);

  /** La fenêtre tient-elle en une seule journée ? Elle décide de l'heure affichée. */
  const memeJournee = registre !== null && registre.from === registre.to;

  const lignes: readonly QueueEntry[] = useMemo(
    () =>
      (registre?.transactions ?? []).map((operation) => {
        // **La seconde ligne de la référence : le libellé, sinon le lien de
        // contre-passe. Jamais les deux, jamais un gabarit vide.** Le libellé
        // est présent sur dix-sept opérations sur cent vingt, le lien sur
        // trois : une ligne rendue systématiquement doublerait la hauteur du
        // tableau pour afficher du blanc neuf fois sur dix.
        const sousLaReference = operation.narrative ?? lienDeReversal(operation, t);
        const frais =
          operation.feeAmount > 0
            ? t('transactions.cells.feeLine', {
                amount: montant(operation.feeAmount, operation.currency),
              })
            : null;
        return {
          id: operation.id,
          reference: operation.publicRef,
          // `values` reste la vérité TEXTUELLE de la ligne : c'est elle que le
          // fichier exporté recopie, et un fichier qui différerait de l'écran
          // ferait douter des deux.
          values: [
            operation.publicRef,
            instant(operation.initiatedAt),
            libelleType(operation.txType),
            libelleCanal(operation.channel),
            montant(operation.amount, operation.currency),
            libelleEtat(operation.status),
            '',
          ],
          cells: [
            <span className="transactions__stack" key="reference">
              <span className="transactions__stack-lead">{operation.publicRef}</span>
              {sousLaReference === null ? null : (
                <span className="transactions__stack-meta">{sousLaReference}</span>
              )}
            </span>,
            // **La date en tête, l'heure en dessous.** Toujours présentes toutes
            // les deux, et c'est la seule seconde ligne inconditionnelle : elle
            // ne coûte rien puisqu'elle remplace une chaîne qui portait déjà les
            // deux, et elle aère une colonne que le dessin voulait étroite.
            <span className="transactions__stack" key="time">
              <span className="transactions__stack-lead">
                {journeeCourte(operation.initiatedAt)}
              </span>
              <span className="transactions__stack-meta">
                {heure(operation.initiatedAt, true)}
              </span>
            </span>,
            libelleType(operation.txType),
            <span className="transactions__stack" key="channel">
              <span className="transactions__stack-lead">
                {libelleCanal(operation.channel)}
              </span>
              <span className="transactions__stack-meta">
                {libelleInitiateur(operation.initiatedByType)}
              </span>
            </span>,
            // **Les frais ne paraissent que s'il y en a.** Un « 0 F CFA »
            // répété sur cent dix-neuf lignes sur cent vingt serait exactement
            // la colonne que le porteur vient de faire retirer, réintroduite
            // sous une autre forme.
            <span className="transactions__stack transactions__stack--end" key="amount">
              <span className="transactions__stack-lead">
                {montant(operation.amount, operation.currency)}
              </span>
              {frais === null ? null : (
                <span className="transactions__stack-meta">{frais}</span>
              )}
            </span>,
            // **Le code d'échec sous la pastille** : c'est le renseignement le
            // plus utile qu'on puisse donner sans ouvrir la ligne, et il n'est
            // porté que par les opérations qui ont échoué — trois sur cent
            // vingt.
            <span className="transactions__stack" key="status">
              <StatusPill status={operation.status} tone={operation.statusTone} />
              {operation.failureCode === null ? null : (
                <span className="transactions__stack-meta">{operation.failureCode}</span>
              )}
            </span>,
            // **L'œil n'est PAS un bouton, et c'est délibéré.** La rangée est
            // déjà cliquable et déjà focalisable : un bouton posé dedans ferait
            // deux arrêts au clavier qui font exactement la même chose, et un
            // lecteur d'écran annoncerait deux fois l'ouverture de la même
            // opération. Ce qui manquait n'était pas une seconde commande —
            // c'était que la première SE VOIE. L'icône est donc un indicateur,
            // masquée aux technologies d'assistance, et le nom accessible de la
            // rangée continue de porter l'action.
            <span className="transactions__open" key="actions" aria-hidden="true">
              <ModuleIcon className="transactions__open-icon" name="eye" />
            </span>,
          ],
        };
      }),
    [
      heure,
      instant,
      journeeCourte,
      libelleCanal,
      libelleEtat,
      libelleInitiateur,
      libelleType,
      montant,
      registre,
      t,
    ],
  );

  /**
   * Les cinq filtres du dessin — et **deux de leurs listes viennent du serveur**.
   *
   * `types` et `statuses` sont les valeurs réellement présentes, avec leur
   * compte : ce ne sont ni un miroir de constantes Java — il n'en existe aucune
   * pour les types d'opération —, ni une liste inventée ici. Chacune s'ouvre par
   * « Tous », de valeur vide : c'est l'absence de filtre, et c'est ce que la
   * maquette écrit en tête de ses deux listes.
   */
  const champsDuFiltre: readonly FormFieldDescriptor[] = useMemo(
    () => [
      {
        name: 'from',
        labelKey: 'transactions.filters.from',
        control: 'date',
        placeholderKey: 'transactions.filters.fromPlaceholder',
      },
      {
        name: 'to',
        labelKey: 'transactions.filters.to',
        control: 'date',
        placeholderKey: 'transactions.filters.toPlaceholder',
      },
      {
        name: 'type',
        labelKey: 'transactions.filters.type',
        control: 'select',
        placeholderKey: 'transactions.filters.allTypes',
        options: [
          { value: '', label: t('transactions.filters.allTypes') },
          ...(registre?.types ?? []).map((facette) => ({
            value: facette.code,
            // Le compte est DANS le libellé : un contrôleur choisit d'autant
            // mieux qu'il sait combien il va trouver.
            label: t('transactions.filters.option', {
              label: libelleType(facette.code),
              count: facette.count,
            }),
          })),
        ],
      },
      {
        name: 'status',
        labelKey: 'transactions.filters.status',
        control: 'select',
        placeholderKey: 'transactions.filters.allStatuses',
        options: [
          { value: '', label: t('transactions.filters.allStatuses') },
          ...(registre?.statuses ?? []).map((facette) => ({
            value: facette.code,
            label: t('transactions.filters.option', {
              label: libelleEtat(facette.code),
              count: facette.count,
            }),
          })),
        ],
      },
      {
        name: 'minAmount',
        labelKey: 'transactions.filters.minAmount',
      },
    ],
    [libelleEtat, libelleType, registre, t],
  );

  /**
   * Ce que la rangée **affiche**, quand c'est le serveur qui l'a décidé.
   *
   * La fenêtre appliquée vient de la réponse : on peut lister sans rien saisir,
   * et les deux pilules de date doivent alors montrer la période que le serveur
   * a retenue plutôt que rester vides sous une table qui, elle, montre une
   * période précise. Les trois autres viennent de l'adresse — c'est elle qui les
   * porte, et le serveur ne les rend pas.
   */
  const valeursDuFiltre = useMemo(
    () =>
      registre === null
        ? undefined
        : {
            from: registre.from,
            to: registre.to,
            type: demande.type,
            status: demande.status,
            minAmount: demande.minAmount,
          },
    [demande, registre],
  );

  // Le nombre de pages se DÉDUIT du total et de la borne — il n'est pas servi,
  // et il n'a pas à l'être. La borne est ramenée à un au minimum, non par
  // défiance mais parce qu'une division par zéro rendrait une infinité de pages.
  const pages =
    registre === null
      ? 1
      : Math.max(1, Math.ceil(registre.total / Math.max(1, registre.pageSize)));

  /** Les en-têtes du fichier : ceux des colonnes qui portent une donnée. */
  const enTetesDuFichier = useMemo(
    () => colonnesExportees.map((colonne) => t(colonne.labelKey)),
    [t],
  );

  /**
   * Écrit la page en main dans un fichier, et **dit combien**.
   *
   * Les valeurs écrites sont celles de la table — ce que l'opérateur voit —, et
   * non les opérations brutes : un fichier qui différerait de l'écran ferait
   * douter des deux.
   */
  const exporterLaPage = useCallback(() => {
    if (registre === null) return;
    const nom = `transactions-${registre.from}.csv`;
    const ecrit = remettreLeFichier(
      nom,
      toCsv(
        enTetesDuFichier,
        lignes.map((ligne) => ligne.values.slice(0, colonnesExportees.length)),
      ),
    );
    setAvis(
      ecrit
        ? t('transactions.actions.exported', { count: lignes.length, name: nom })
        : t('transactions.actions.exportImpossible'),
    );
  }, [enTetesDuFichier, lignes, registre, t]);

  const exportDeLaBarre = useMemo(() => {
    if (registre === null || registre.transactions.length === 0) return null;
    return exporterLaPage;
  }, [exporterLaPage, registre]);
  useDeclareExport(exportDeLaBarre);

  /**
   * Ouvrir une ligne mène à **sa fiche**, et n'ouvre aucun panneau.
   *
   * Le porteur l'a demandé — « le mieux va être de créer une page détail à
   * part » —, et c'est aussi ce qui garde la trace juste : la fiche est une
   * seconde lecture, journalisée avec la référence de l'opération en objet. Un
   * panneau alimenté par la ligne déjà reçue aurait montré une opération que le
   * journal ne mentionne pas.
   */
  const ouvrir = useCallback(
    (ligne: QueueEntry) => {
      naviguer(transactionRoute(ligne.id));
    },
    [naviguer],
  );

  return (
    <section className="transactions" aria-label={t('transactions.landmark')}>
      {avis === null ? null : (
        <p className="transactions__outcome" role="status" data-testid="transactions-outcome">
          {avis}
        </p>
      )}

      {/* La rangée de filtres du dessin : les cinq contrôles et la commande sur
          UNE ligne, en tête de la carte, et non cinq blocs empilés sur toute la
          largeur. C'est ce qui distingue le plus un écran de back-office d'un
          formulaire. */}
      <FormShell
        fields={champsDuFiltre}
        layout="inline"
        values={valeursDuFiltre}
        onSubmit={filtrer}
        submitLabelKey="transactions.filters.submit"
      />

      <div className="transactions__chipbar">
        {/* **Les raccourcis de période.** Le dessin n'en a pas — il a deux
            calendriers —, et ils sont ajoutés parce que le porteur a vu la page
            vide : ouvrir deux calendriers pour regarder la semaine est un geste
            de trop, répété toute la journée. L'état actif se lit sur la fenêtre
            SERVIE, jamais sur un souvenir local : c'est le serveur qui décide
            de la période, et la pastille doit dire ce qu'il a appliqué. */}
        <div
          className="transactions__ranges"
          role="group"
          aria-label={t('transactions.ranges.landmark')}
        >
          {quickRanges.map((jours) => (
            <button
              key={jours}
              type="button"
              className="transactions__chip"
              aria-pressed={joursDeLaFenetre === jours}
              onClick={() => {
                appliquerLaPeriode(jours);
              }}
            >
              {t('transactions.ranges.days', { count: jours })}
            </button>
          ))}
        </div>

        {/* **Les pastilles d'état, et elles comptent LA MÊME CHOSE que les
            cartes.** Une revue les a mesurées : après une recherche, les cartes
            annonçaient « Transactions 10 · Échouées 0 » pendant que les
            pastilles, quarante points au-dessus, affichaient encore
            « Échouée · 3 ». Même écran, même instant, deux vérités — et un
            compte qui contredit le compte d'à côté est pire qu'un compte absent.

            Le serveur compte donc les facettes sur la fenêtre, l'acteur et la
            recherche, mais **pas** sur le type ni l'état : une pastille choisie
            qui viderait toutes les autres empêcherait de jamais élargir. Le nom
            du groupe le dit, pour que la règle se lise au lieu de se
            deviner. */}
        <div
          className="transactions__chips"
          role="group"
          aria-label={t('transactions.chips.landmark')}
        >
          <button
            type="button"
            className="transactions__chip"
            aria-pressed={demande.status === ''}
            onClick={() => {
              poser({ status: '' });
            }}
          >
            {t('transactions.chips.all')}
          </button>
          {(registre?.statuses ?? []).map((facette) => (
            <button
              key={facette.code}
              type="button"
              className="transactions__chip"
              data-status={facette.code}
              data-tone={facette.tone}
              aria-pressed={demande.status === facette.code}
              onClick={() => {
                poser({ status: facette.code });
              }}
            >
              {t('transactions.chips.withCount', {
                label: libelleEtat(facette.code),
                count: facette.count,
              })}
            </button>
          ))}
        </div>
      </div>

      {erreur === null ? null : (
        <p className="transactions__notice" role="alert">
          {t(erreur)}
        </p>
      )}

      {registre === null ? (
        erreur === null ? (
          <p className="transactions__idle">{t('transactions.loading')}</p>
        ) : null
      ) : (
        <>
          <TransactionStats page={registre} formatAmount={montant} formatRate={taux} />

          {registre.transactions.length === 0 ? (
            <p className="transactions__idle">{t('transactions.empty')}</p>
          ) : (
            <QueueShell
              columns={transactionColumns}
              entries={lignes}
              onOpen={ouvrir}
              /* **Le pied du dessin, DANS la carte.** À gauche le compte, à
                 droite la pagination. Le poser sous la carte ferait lire la
                 page comme trois blocs empilés au lieu d'un tableau. */
              footer={
                <Pagination
                  block="transactions"
                  keyPrefix="transactions.pages"
                  page={registre.page}
                  pages={pages}
                  pageSize={registre.pageSize}
                  onGo={(rang) => {
                    poser({ page: rang === PREMIERE_PAGE ? '' : String(rang) });
                  }}
                  /* Le compte est DANS la rangée, et non à côté d'elle : c'est
                     la pagination que le porteur a choisie, où « Affichage 1 à
                     20 sur 730 » tient sur la même ligne que les contrôles. */
                  summary={
                    <span className="transactions__count" data-testid="transactions-count">
                      {memeJournee
                        ? t('transactions.countOfDay', {
                            shown: registre.transactions.length,
                            total: registre.total,
                            day: journee(registre.from),
                          })
                        : t('transactions.countOfRange', {
                            shown: registre.transactions.length,
                            total: registre.total,
                            from: journee(registre.from),
                            to: journee(registre.to),
                          })}
                    </span>
                  }
                />
              }
            />
          )}
        </>
      )}
    </section>
  );
}

/**
 * Le lien de réversal d'une opération, ou `null`.
 *
 * **C'est le seul ajout de ce chantier qui entre dans le tableau**, et il
 * n'ajoute pas de colonne : il prend une seconde ligne dans la cellule de
 * référence. La raison est qu'un contrôleur doit voir *sans ouvrir* qu'une
 * opération a été contre-passée — une opération échouée déjà traitée et une qui
 * attend encore ne se regardent pas de la même façon, et les distinguer une
 * fiche à la fois ferait rouvrir la file entière.
 */
function lienDeReversal(
  operation: Transaction,
  t: (key: string, options?: Record<string, unknown>) => string,
): string | null {
  if (operation.reversesRef !== null) {
    return t('transactions.cells.reverses', { reference: operation.reversesRef });
  }
  if (operation.reversedByRef !== null) {
    return t('transactions.cells.reversedBy', { reference: operation.reversedByRef });
  }
  return null;
}

/**
 * Les indicateurs du dessin — en **cartes**, et tous servis.
 *
 * Le premier livré les rendait sur une ligne de texte ; le porteur a regardé
 * l'écran et l'a dit. Ce sont des cartes, et elles se recalculent avec les
 * filtres **parce que le serveur les recalcule** : chacune sort du même
 * `SELECT` que la page, sur la même fenêtre et les mêmes critères.
 *
 * **Le taux de succès n'est pas calculé ici**, et c'est ce qui permet de dire
 * la seule chose qui compte : sur une fenêtre vide, `0 / 0` ne vaut pas « 0 % de
 * succès » — il vaut « aucune opération ». Un « 0 % » affiché sur une journée
 * sans activité se lirait comme une panne générale du système de paiement, et
 * le serveur rend `null` exprès pour que l'écran puisse le dire.
 *
 * **La carte des frais reste, alors que la colonne a été retirée**, et c'est la
 * décision du porteur telle qu'il l'a posée : il retire la colonne, pas
 * l'information. Le total des frais sur la période a du sens en tête de page ;
 * la colonne, sur un registre où la quasi-totalité des opérations sont à zéro,
 * n'en avait pas.
 */
function TransactionStats({
  page,
  formatAmount,
  formatRate,
}: {
  readonly page: TransactionPage;
  readonly formatAmount: (minorUnits: number, currency: string | null) => string;
  readonly formatRate: (value: number | null) => string;
}): ReactNode {
  const { t } = useTranslation();
  // La devise du volume est celle de la première opération qui en porte une :
  // le registre n'a pas de devise propre, et la déduire d'une opération est la
  // seule façon honnête de l'obtenir. Sans aucune, le volume est en unités
  // mineures, nommées comme telles.
  const devise =
    page.transactions.find((operation) => operation.currency !== null)?.currency ?? null;

  const cartes: readonly {
    readonly id: string;
    readonly label: string;
    readonly value: string;
    readonly tone?: string;
  }[] = [
    { id: 'count', label: t('transactions.stats.count'), value: String(page.total) },
    {
      id: 'volume',
      label: t('transactions.stats.volume'),
      value: formatAmount(page.volume, devise),
    },
    {
      id: 'fees',
      label: t('transactions.stats.fees'),
      value: formatAmount(page.fees, devise),
    },
    {
      id: 'success',
      label: t('transactions.stats.successRate'),
      value: formatRate(page.successRate),
      // Une carte sans mesure ne se peint pas en vert : « aucune opération »
      // n'est pas une bonne nouvelle, c'est une absence de nouvelle.
      tone: page.successRate === null ? 'OTHER' : 'SUCCEEDED',
    },
    {
      id: 'inflight',
      label: t('transactions.stats.inFlight'),
      value: String(page.inFlight),
      tone: page.inFlight === 0 ? 'OTHER' : 'IN_FLIGHT',
    },
    {
      id: 'failed',
      label: t('transactions.stats.failed'),
      value: String(page.failed),
      tone: page.failed === 0 ? 'OTHER' : 'FAILED',
    },
  ];

  return (
    <ul className="transactions__stats" aria-label={t('transactions.stats.landmark')}>
      {cartes.map((carte) => (
        <li className="transactions__stat" key={carte.id} data-tone={carte.tone}>
          <span className="transactions__stat-label">{carte.label}</span>
          <span
            className="transactions__stat-value"
            data-testid={`transactions-stat-${carte.id}`}
          >
            {carte.value}
          </span>
        </li>
      ))}
    </ul>
  );
}
